import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, Subject, interval, takeUntil } from 'rxjs';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { finalize } from 'rxjs/operators';
import { NativeHostService } from 'src/app/services/native-host.service';
import { NativeCapturePermissions } from 'src/app/interfaces/native-host';

export type RecordingState = 'idle' | 'countdown' | 'recording' | 'paused' | 'processing' | 'uploading' | 'complete' | 'error';

export type WebcamPosition = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';

export type StreamWarning = 'none' | 'screen-frozen' | 'webcam-frozen' | 'audio-lost' | 'low-framerate';

export interface RecordingConfig {
  /** Enable screen capture */
  captureScreen: boolean;
  /** Enable webcam capture */
  captureWebcam: boolean;
  /** Enable audio capture */
  captureAudio: boolean;
  /** Position of webcam bubble overlay */
  webcamPosition: WebcamPosition;
  /** Size of webcam bubble in pixels */
  webcamSize: number;
  /** Border width of webcam bubble in pixels */
  webcamBorderWidth: number;
  /** Countdown seconds before recording starts (0 = no countdown) */
  countdownSeconds: number;
  /** User ID for storage path */
  userId: string;
  /** Optional folder name for organizing recordings */
  folder?: string;
  /** Optional title/name for the recording */
  title?: string;
  /** Audio device ID to use for recording */
  audioDeviceId?: string;
  /** Native desktop source ID when Electron capture is active */
  nativeSourceId?: string;
}

export interface RecordingResult {
  /** Download URL of the uploaded video */
  downloadUrl: string;
  /** Firestore document ID of the media record */
  mediaId: string;
  /** Duration of the recording in seconds */
  duration: number;
  /** Timestamp when recording was created */
  createdAt: Date;
}

export interface ScreenRecordingHistoryItem {
  id: string;
  displayName: string;
  url: string | null;
  type: string;
  duration: number | null;
  size: number | null;
  createdAt: Date | null;
}

export interface BrowserCapabilities {
  /** Whether getDisplayMedia is supported */
  screenCapture: boolean;
  /** Whether getUserMedia for video is supported */
  webcam: boolean;
  /** Whether getUserMedia for audio is supported */
  audio: boolean;
  /** Whether MediaRecorder is supported */
  mediaRecorder: boolean;
  /** Whether all required APIs are available */
  isFullySupported: boolean;
  /** Human-readable message about compatibility */
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class ScreenRecorderService {
  // State observables
  private _state = new BehaviorSubject<RecordingState>('idle');
  private _recordingTime = new BehaviorSubject<number>(0);
  private _uploadProgress = new BehaviorSubject<number>(0);
  private _countdown = new BehaviorSubject<number>(0);
  private _downloadUrl = new BehaviorSubject<string | null>(null);
  private _mediaId = new BehaviorSubject<string | null>(null);
  private _error = new BehaviorSubject<string | null>(null);
  private _isModalVisible = new BehaviorSubject<boolean>(false);
  private _isMinimized = new BehaviorSubject<boolean>(false);
  private _isPaused = new BehaviorSubject<boolean>(false);
  private _streamWarning = new BehaviorSubject<StreamWarning>('none');
  private _actualFps = new BehaviorSubject<number>(30);

  public state$: Observable<RecordingState> = this._state.asObservable();
  public recordingTime$: Observable<number> = this._recordingTime.asObservable();
  public uploadProgress$: Observable<number> = this._uploadProgress.asObservable();
  public countdown$: Observable<number> = this._countdown.asObservable();
  public downloadUrl$: Observable<string | null> = this._downloadUrl.asObservable();
  public mediaId$: Observable<string | null> = this._mediaId.asObservable();
  public error$: Observable<string | null> = this._error.asObservable();
  public isModalVisible$: Observable<boolean> = this._isModalVisible.asObservable();
  public isMinimized$: Observable<boolean> = this._isMinimized.asObservable();
  public isPaused$: Observable<boolean> = this._isPaused.asObservable();
  public streamWarning$: Observable<StreamWarning> = this._streamWarning.asObservable();
  public actualFps$: Observable<number> = this._actualFps.asObservable();

  // Internal recording state
  private screenStream: MediaStream | null = null;
  private webcamStream: MediaStream | null = null;
  private audioStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private recordingStartTime: number = 0;
  private timerSubscription: any = null;
  private stopTimer$ = new Subject<void>();
  private currentConfig: RecordingConfig | null = null;
  
  // Canvas compositing
  private compositeCanvas: HTMLCanvasElement | null = null;
  private compositeCtx: CanvasRenderingContext2D | null = null;
  private screenVideo: HTMLVideoElement | null = null;
  private webcamVideo: HTMLVideoElement | null = null;
  private animationFrameId: number | null = null;
  private isCompositingActive: boolean = false;
  private canvasCaptureTrack: CanvasCaptureMediaStreamTrack | null = null;
  
  // Stream health monitoring
  private streamHealthInterval: any = null;
  private lastScreenFrameTime: number = 0;
  private lastWebcamFrameTime: number = 0;
  
  // FPS tracking
  private frameCount: number = 0;
  private fpsLastCalculated: number = 0;
  
  // Visibility change handler reference (for cleanup)
  private visibilityChangeHandler: (() => void) | null = null;
  private compositeIntervalId: any = null;

  // Callbacks
  private onComplete: ((result: RecordingResult) => void) | null = null;
  private isNativeRecording: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private afStorage: AngularFireStorage,
    private afs: AngularFirestore,
    private nativeHostService: NativeHostService
  ) {
    this.nativeHostService.screenRecordingComplete$.subscribe(async (payload) => {
      if (!this.isNativeRecording || !this.currentConfig) {
        return;
      }

      const mimeType = payload.mimeType || 'video/webm';
      const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
      const blob = new Blob([payload.buffer], { type: mimeType });
      const duration = Math.max(1, Math.floor(payload.durationMs / 1000));

      this.isNativeRecording = false;
      await this.uploadRecording(blob, extension, duration);
    });

    this.nativeHostService.screenRecordingError$.subscribe((payload) => {
      if (!this.isNativeRecording) {
        return;
      }

      this.isNativeRecording = false;
      this._error.next(payload.message || 'Native recording failed.');
      this._state.next('error');
    });
  }

  get isElectronDesktop(): boolean {
    return this.nativeHostService.isElectronDesktop();
  }

  get nativeDesktopAvailable$(): Observable<boolean> {
    return this.nativeHostService.desktopAvailable$;
  }

  /**
   * Check browser capabilities for screen recording
   */
  checkBrowserCapabilities(): BrowserCapabilities {
    if (!isPlatformBrowser(this.platformId)) {
      return {
        screenCapture: false,
        webcam: false,
        audio: false,
        mediaRecorder: false,
        isFullySupported: false,
        message: 'Screen recording is not available during server-side rendering.'
      };
    }

    if (this.nativeHostService.isElectronDesktop()) {
      const nav = navigator as any;
      const webcam = !!(nav.mediaDevices && nav.mediaDevices.getUserMedia);

      return {
        screenCapture: true,
        webcam,
        audio: webcam,
        mediaRecorder: true,
        isFullySupported: true,
        message: 'Platfio desktop recording is available through the native shell with optional microphone and webcam overlay.'
      };
    }

    const nav = navigator as any;
    const screenCapture = !!(nav.mediaDevices && nav.mediaDevices.getDisplayMedia);
    const webcam = !!(nav.mediaDevices && nav.mediaDevices.getUserMedia);
    const audio = webcam; // Same API
    const mediaRecorder = typeof MediaRecorder !== 'undefined';

    const isFullySupported = screenCapture && mediaRecorder;

    let message = '';
    if (isFullySupported) {
      message = 'Your browser supports screen recording.';
    } else if (!screenCapture) {
      message = 'Your browser does not support screen capture. Please use Chrome, Edge, or Firefox.';
    } else if (!mediaRecorder) {
      message = 'Your browser does not support media recording. Please update your browser.';
    }

    return {
      screenCapture,
      webcam,
      audio,
      mediaRecorder,
      isFullySupported,
      message
    };
  }

  /**
   * Open the recording modal
   */
  openModal(): void {
    this._isModalVisible.next(true);
    this._isMinimized.next(false);
  }

  /**
   * Close the recording modal
   */
  closeModal(): void {
    // Only allow closing if not currently recording
    if (this._state.value === 'recording' || this._state.value === 'countdown') {
      // Minimize instead of close
      this._isMinimized.next(true);
      return;
    }
    this._isModalVisible.next(false);
    this._isMinimized.next(false);
    this.cleanup();
  }

  /**
   * Minimize the modal (shows indicator only)
   */
  minimizeModal(): void {
    this._isMinimized.next(true);
  }

  /**
   * Restore the modal from minimized state
   */
  restoreModal(): void {
    this._isMinimized.next(false);
  }

  /**
   * Start the recording process
   */
  async startRecording(config: RecordingConfig, onComplete?: (result: RecordingResult) => void): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      this._error.next('Recording is not available in this environment.');
      return;
    }

    const capabilities = this.checkBrowserCapabilities();
    if (!capabilities.isFullySupported) {
      this._error.next(capabilities.message);
      this._state.next('error');
      return;
    }

    this.currentConfig = config;
    this.onComplete = onComplete || null;
    this._error.next(null);
    this._downloadUrl.next(null);
    this._mediaId.next(null);
    this.recordedChunks = [];

    if (this.shouldUseNativeDesktopCapture(config)) {
      await this.startNativeRecording(config, onComplete);
      return;
    }

    try {
      // Request screen capture
      if (config.captureScreen) {
        this.screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30 }
          },
          audio: false // We'll capture system audio separately if needed
        });

        // Handle user stopping screen share via browser UI
        this.screenStream.getVideoTracks()[0].onended = () => {
          this.stopRecording();
        };
      }

      // Request webcam
      if (config.captureWebcam) {
        try {
          this.webcamStream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 320 },
              height: { ideal: 320 },
              facingMode: 'user'
            },
            audio: false
          });
        } catch (e) {
          console.warn('Webcam not available:', e);
          // Continue without webcam
        }
      }

      // Request audio
      if (config.captureAudio) {
        try {
          this.audioStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              sampleRate: 44100
            },
            video: false
          });
        } catch (e) {
          console.warn('Microphone not available:', e);
          // Continue without audio
        }
      }

      // Start countdown or begin recording immediately
      if (config.countdownSeconds > 0) {
        await this.runCountdown(config.countdownSeconds);
      }

      // Setup compositing only when the recording needs an overlay.
      if (this.shouldUseCanvasCompositing()) {
        await this.setupCompositing(config);
      }
      this.beginRecording();

    } catch (error: any) {
      console.error('Error starting recording:', error);
      this._error.next(error.message || 'Failed to start recording. Please check your permissions.');
      this._state.next('error');
      this.cleanup();
    }
  }

  /**
   * Start recording with pre-acquired streams from the modal.
   * This allows the modal to handle stream acquisition separately for live preview,
   * then hand off the streams to the service for actual recording.
   */
  async startRecordingWithStreams(
    config: RecordingConfig,
    screenStream: MediaStream | null,
    webcamStream: MediaStream | null,
    audioStream: MediaStream | null,
    onComplete?: (result: RecordingResult) => void
  ): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      this._error.next('Recording is not available in this environment.');
      return;
    }

    const capabilities = this.checkBrowserCapabilities();
    if (!capabilities.isFullySupported) {
      this._error.next(capabilities.message);
      this._state.next('error');
      return;
    }

    if (this.shouldUseNativeDesktopCapture(config)) {
      await this.startNativeRecording(config, onComplete);
      return;
    }

    if (!screenStream) {
      this._error.next('Screen capture stream is required.');
      this._state.next('error');
      return;
    }

    this.currentConfig = config;
    this.onComplete = onComplete || null;
    this._error.next(null);
    this._downloadUrl.next(null);
    this._mediaId.next(null);
    this.recordedChunks = [];

    // Take ownership of the streams
    this.screenStream = screenStream;
    this.webcamStream = webcamStream;
    this.audioStream = audioStream;

    // Handle user stopping screen share via browser UI
    this.screenStream.getVideoTracks()[0].onended = () => {
      this.stopRecording();
    };

    try {
      // Start countdown or begin recording immediately
      if (config.countdownSeconds > 0) {
        await this.runCountdown(config.countdownSeconds);
      }

      // Setup compositing only when the recording needs an overlay.
      if (this.shouldUseCanvasCompositing()) {
        await this.setupCompositing(config);
      }
      this.beginRecording();

    } catch (error: any) {
      console.error('Error starting recording:', error);
      this._error.next(error.message || 'Failed to start recording. Please check your permissions.');
      this._state.next('error');
      this.cleanup();
    }
  }

  async startNativeShortcutRecording(config: RecordingConfig): Promise<void> {
    await this.startNativeRecording(config);
  }

  private shouldUseNativeDesktopCapture(config: RecordingConfig): boolean {
    return this.nativeHostService.isElectronDesktop() && config.captureScreen;
  }

  private async startNativeRecording(config: RecordingConfig, onComplete?: (result: RecordingResult) => void): Promise<void> {
    this.currentConfig = config;
    this.onComplete = onComplete || null;
    this._error.next(null);
    this._downloadUrl.next(null);
    this._mediaId.next(null);
    this.recordedChunks = [];

    try {
      const permissions = await this.nativeHostService.getCapturePermissions();
      if (permissions && !permissions.canRecord) {
        throw new Error(permissions.message);
      }

      if (config.countdownSeconds > 0) {
        await this.runCountdown(config.countdownSeconds);
      }

      const result = await this.nativeHostService.startScreenRecording({
        captureAudio: config.captureAudio,
        captureWebcam: config.captureWebcam,
        frameRate: 30,
        height: 1080,
        sourceId: config.nativeSourceId,
        webcamBorderWidth: config.webcamBorderWidth,
        webcamPosition: config.webcamPosition,
        webcamSize: config.webcamSize,
        width: 1920
      });

      if (!result.ok) {
        throw new Error(result.message || 'Failed to start native screen recording.');
      }

      this.isNativeRecording = true;
      this._state.next('recording');
      this._isMinimized.next(true);
      this.recordingStartTime = Date.now();
      this.startRecordingTimer();
    } catch (error: any) {
      this.isNativeRecording = false;
      this._error.next(error.message || 'Failed to start native screen recording.');
      this._state.next('error');
    }
  }

  /**
   * Run countdown before recording
   */
  private runCountdown(seconds: number): Promise<void> {
    return new Promise((resolve) => {
      this._state.next('countdown');
      this._countdown.next(seconds);

      const countdownInterval = setInterval(() => {
        const current = this._countdown.value;
        if (current <= 1) {
          clearInterval(countdownInterval);
          this._countdown.next(0);
          resolve();
        } else {
          this._countdown.next(current - 1);
        }
      }, 1000);
    });
  }

  /**
   * Setup canvas compositing for combining screen + webcam
   */
  private async setupCompositing(config: RecordingConfig): Promise<void> {
    // Create hidden video elements to play streams
    if (this.screenStream) {
      this.screenVideo = document.createElement('video');
      this.screenVideo.srcObject = this.screenStream;
      this.screenVideo.muted = true;
      this.screenVideo.playsInline = true;
      
      // Critical: Handle video element errors
      this.screenVideo.onerror = (e) => {
        console.error('Screen video element error:', e);
        this.handleStreamError('Screen capture error - video element failed');
      };
      
      // Handle video stall (not receiving frames)
      this.screenVideo.onstalled = () => {
        console.warn('Screen video stalled - attempting to recover');
        this.attemptVideoRecovery(this.screenVideo!, 'screen');
      };
      
      await this.screenVideo.play();
      
      // Monitor screen video track health
      const screenTrack = this.screenStream.getVideoTracks()[0];
      if (screenTrack) {
        screenTrack.onended = () => {
          console.log('Screen track ended');
          this.stopRecording();
        };
        screenTrack.onmute = () => {
          console.warn('Screen track muted');
        };
        screenTrack.onunmute = () => {
          console.log('Screen track unmuted');
        };
      }
    }

    if (this.webcamStream) {
      this.webcamVideo = document.createElement('video');
      this.webcamVideo.srcObject = this.webcamStream;
      this.webcamVideo.muted = true;
      this.webcamVideo.playsInline = true;
      
      // Handle webcam video element errors
      this.webcamVideo.onerror = (e) => {
        console.warn('Webcam video element error:', e);
        // Don't stop recording for webcam issues, just log
      };
      
      this.webcamVideo.onstalled = () => {
        console.warn('Webcam video stalled - attempting to recover');
        this.attemptVideoRecovery(this.webcamVideo!, 'webcam');
      };
      
      await this.webcamVideo.play();
      
      // Monitor webcam video track health
      const webcamTrack = this.webcamStream.getVideoTracks()[0];
      if (webcamTrack) {
        webcamTrack.onended = () => {
          console.warn('Webcam track ended - continuing without webcam');
          // Don't stop recording, just disable webcam overlay
          if (this.webcamVideo) {
            this.webcamVideo.srcObject = null;
          }
          this.webcamStream = null;
        };
        webcamTrack.onmute = () => {
          console.warn('Webcam track muted');
        };
      }
    }

    // Create composite canvas
    this.compositeCanvas = document.createElement('canvas');
    
    // Use screen dimensions or default to 1920x1080
    const screenTrack = this.screenStream?.getVideoTracks()[0];
    const settings = screenTrack?.getSettings();
    this.compositeCanvas.width = settings?.width || 1920;
    this.compositeCanvas.height = settings?.height || 1080;
    
    this.compositeCtx = this.compositeCanvas.getContext('2d', {
      alpha: false
    });
  }
  
  /**
   * Attempt to recover a stalled video element
   */
  private attemptVideoRecovery(video: HTMLVideoElement, source: string): void {
    if (!video.srcObject) return;
    
    const currentTime = video.currentTime;
    video.pause();
    
    setTimeout(() => {
      if (video.srcObject) {
        video.play().then(() => {
          console.log(`${source} video recovered successfully`);
        }).catch(err => {
          console.error(`Failed to recover ${source} video:`, err);
          if (source === 'screen') {
            this.handleStreamError('Screen capture recovery failed');
          }
        });
      }
    }, 100);
  }
  
  /**
   * Handle stream errors during recording
   */
  private handleStreamError(message: string): void {
    console.error('Stream error:', message);
    // Don't immediately stop - try to continue if possible
    // Only stop if screen stream is completely dead
    if (this.screenStream) {
      const tracks = this.screenStream.getVideoTracks();
      const allDead = tracks.every(t => t.readyState === 'ended' || t.muted);
      if (allDead) {
        this._error.next(message);
        this.stopRecording();
      }
    }
  }

  /**
   * Begin the actual recording
   */
  private beginRecording(): void {
    if (!this.shouldRecordScreenStreamDirectly() && (!this.compositeCanvas || !this.compositeCtx)) {
      this._error.next('Failed to initialize recording canvas.');
      this._state.next('error');
      return;
    }

    const recordingStream = this.createRecordingStream();
    if (!recordingStream) {
      this._error.next('Failed to initialize recording stream.');
      this._state.next('error');
      return;
    }

    if (!this.shouldRecordScreenStreamDirectly()) {
      this.startCompositing();
    }

    // Determine supported MIME type. WebM is the most reliable browser
    // MediaRecorder path for canvas/display streams; Safari will fall back to MP4.
    const mimeTypes = [
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=vp9,opus',
      'video/webm',
      'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
      'video/mp4'
    ];

    let selectedMimeType = '';
    for (const mimeType of mimeTypes) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        selectedMimeType = mimeType;
        break;
      }
    }

    if (!selectedMimeType) {
      this._error.next('No supported video format found in this browser.');
      this._state.next('error');
      return;
    }
    
    console.log('Using MIME type:', selectedMimeType);

    // Setup MediaRecorder with optimized settings
    try {
      this.mediaRecorder = new MediaRecorder(recordingStream, {
        mimeType: selectedMimeType,
        videoBitsPerSecond: 2500000 // 2.5 Mbps
      });
    } catch (e) {
      console.warn('Failed to create MediaRecorder with options, trying without:', e);
      // Fallback without specifying mimeType
      this.mediaRecorder = new MediaRecorder(recordingStream);
    }

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.recordedChunks.push(event.data);
        // Log chunk receipt for debugging long recordings
        console.log(`Recorded chunk: ${event.data.size} bytes (total chunks: ${this.recordedChunks.length})`);
      }
    };

    this.mediaRecorder.onstop = () => {
      this.handleRecordingComplete();
    };

    this.mediaRecorder.onerror = (event: any) => {
      console.error('MediaRecorder error:', event);
      const errorName = event.error?.name || 'Unknown';
      const errorMessage = event.error?.message || 'Recording error occurred';
      this._error.next(`Recording error: ${errorName} - ${errorMessage}`);
      this._state.next('error');
    };
    
    // Monitor MediaRecorder state changes
    this.mediaRecorder.onpause = () => {
      console.log('MediaRecorder paused');
    };
    
    this.mediaRecorder.onresume = () => {
      console.log('MediaRecorder resumed');
    };
    
    this.mediaRecorder.onstart = () => {
      console.log('MediaRecorder started');
    };

    // Set state to recording BEFORE starting the media recorder
    this._state.next('recording');
    
    // Auto-minimize modal so user can start presenting immediately
    this._isMinimized.next(true);
    
    // Start recording with 5-second timeslice for chunks
    // Larger timeslice = more efficient encoding, less overhead
    // But still small enough to recover most data if something goes wrong
    this.mediaRecorder.start(5000);
    this.recordingStartTime = Date.now();
    
    console.log('Recording started with 5-second chunks');

    // Start recording timer
    this.startRecordingTimer();
  }

  private shouldRecordScreenStreamDirectly(): boolean {
    return !!this.screenStream && !this.webcamStream;
  }

  private shouldUseCanvasCompositing(): boolean {
    return !this.shouldRecordScreenStreamDirectly();
  }

  private createRecordingStream(): MediaStream | null {
    if (this.shouldRecordScreenStreamDirectly()) {
      const videoTracks = this.screenStream?.getVideoTracks() || [];
      if (videoTracks.length === 0) {
        return null;
      }

      const directStream = new MediaStream(videoTracks);
      this.addAudioTracks(directStream);
      return directStream;
    }

    if (!this.compositeCanvas) {
      return null;
    }

    let canvasStream = this.compositeCanvas.captureStream(0);
    const captureTrack = canvasStream.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack | undefined;
    if (captureTrack && typeof captureTrack.requestFrame === 'function') {
      this.canvasCaptureTrack = captureTrack;
    } else {
      canvasStream.getTracks().forEach(track => track.stop());
      canvasStream = this.compositeCanvas.captureStream(30);
      this.canvasCaptureTrack = null;
    }

    this.addAudioTracks(canvasStream);
    return canvasStream;
  }

  private addAudioTracks(recordingStream: MediaStream): void {
    if (!this.audioStream) {
      return;
    }

    this.audioStream.getAudioTracks().forEach(track => {
      recordingStream.addTrack(track);
      track.onended = () => {
        console.warn('Audio track ended unexpectedly');
      };
    });
  }

  /**
   * Start the compositing animation loop
   * Uses a hybrid approach: requestAnimationFrame when tab is visible,
   * falls back to setInterval when tab is hidden to prevent throttling
   */
  private startCompositing(): void {
    this.isCompositingActive = true;
    this.lastScreenFrameTime = Date.now();
    this.lastWebcamFrameTime = Date.now();
    this.frameCount = 0;
    this.fpsLastCalculated = Date.now();
    this._streamWarning.next('none');
    
    let lastDrawTime = 0;
    const targetFps = 30;
    const frameInterval = 1000 / targetFps;
    let useInterval = false;
    
    const draw = (timestamp?: number) => {
      if (!this.compositeCtx || !this.compositeCanvas || !this.isCompositingActive) {
        return;
      }

      // Throttle to target FPS
      const now = timestamp || performance.now();
      if (now - lastDrawTime < frameInterval) {
        if (!useInterval && this.isCompositingActive) {
          this.animationFrameId = requestAnimationFrame(draw);
        }
        return;
      }
      lastDrawTime = now;
      
      // Track actual FPS
      this.frameCount++;
      const fpsElapsed = Date.now() - this.fpsLastCalculated;
      if (fpsElapsed >= 1000) {
        const actualFps = Math.round((this.frameCount / fpsElapsed) * 1000);
        this._actualFps.next(actualFps);
        
        // Warn if FPS is too low (below 10 fps)
        if (actualFps < 10 && this._streamWarning.value === 'none') {
          console.warn(`Low framerate detected: ${actualFps} fps`);
          this._streamWarning.next('low-framerate');
        } else if (actualFps >= 15 && this._streamWarning.value === 'low-framerate') {
          // Clear warning immediately when FPS recovers
          this._streamWarning.next('none');
        }
        
        this.frameCount = 0;
        this.fpsLastCalculated = Date.now();
      }

      const ctx = this.compositeCtx;
      const canvas = this.compositeCanvas;

      // Clear canvas
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw screen capture - check if video is actually playing
      if (this.screenVideo && this.screenStream) {
        // Check if video element has valid dimensions (means it's receiving frames)
        if (this.screenVideo.videoWidth > 0 && this.screenVideo.videoHeight > 0) {
          try {
            ctx.drawImage(this.screenVideo, 0, 0, canvas.width, canvas.height);
            this.lastScreenFrameTime = Date.now();

            if (this._streamWarning.value === 'screen-frozen' || this._streamWarning.value === 'low-framerate') {
              this._streamWarning.next('none');
            }
          } catch (e) {
            console.warn('Error drawing screen frame:', e);
          }
        } else {
          // Video dimensions are 0 - stream might be dead
          console.warn('Screen video has no dimensions - stream may be dead');
        }
      }

      // Draw webcam bubble - with error handling
      if (this.webcamVideo && this.webcamStream && this.currentConfig) {
        // Only draw if webcam has valid frames
        if (this.webcamVideo.videoWidth > 0 && this.webcamVideo.videoHeight > 0) {
          try {
            this.drawWebcamBubble(ctx, canvas);
            this.lastWebcamFrameTime = Date.now();
          } catch (e) {
            console.warn('Error drawing webcam frame:', e);
          }
        }
      }

      this.canvasCaptureTrack?.requestFrame();

      // Continue animation loop
      if (this.isCompositingActive) {
        if (!useInterval) {
          this.animationFrameId = requestAnimationFrame(draw);
        }
      }
    };
    
    // Handle visibility change - switch to interval when tab is hidden
    // This is CRITICAL because requestAnimationFrame throttles to ~1fps when tab is hidden
    this.visibilityChangeHandler = () => {
      if (document.hidden && this.isCompositingActive) {
        // Tab is hidden - switch to setInterval to maintain frame rate
        console.log('Tab hidden - switching to interval-based compositing');
        useInterval = true;
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }
        if (!this.compositeIntervalId) {
          this.compositeIntervalId = setInterval(() => draw(), frameInterval);
        }
        draw();
      } else if (!document.hidden && this.isCompositingActive) {
        // Tab is visible again - switch back to requestAnimationFrame
        console.log('Tab visible - switching back to requestAnimationFrame');
        useInterval = false;
        if (this.compositeIntervalId) {
          clearInterval(this.compositeIntervalId);
          this.compositeIntervalId = null;
        }
        if (!this.animationFrameId) {
          this.animationFrameId = requestAnimationFrame(draw);
        }
      }
    };
    
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    
    // Start stream health monitoring
    this.startStreamHealthMonitoring();
    
    if (document.hidden) {
      this.visibilityChangeHandler();
    } else {
      draw();
    }
  }
  
  /**
   * Draw the webcam bubble overlay
   */
  private drawWebcamBubble(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement): void {
    if (!this.webcamVideo || !this.currentConfig) return;
    
    const bubbleSize = this.currentConfig.webcamSize || 200;
    const borderWidth = this.currentConfig.webcamBorderWidth ?? 3;
    const padding = 20;
    let x = padding;
    let y = canvas.height - bubbleSize - padding;

    switch (this.currentConfig.webcamPosition) {
      case 'bottom-right':
        x = canvas.width - bubbleSize - padding;
        y = canvas.height - bubbleSize - padding;
        break;
      case 'top-left':
        x = padding;
        y = padding;
        break;
      case 'top-right':
        x = canvas.width - bubbleSize - padding;
        y = padding;
        break;
      case 'bottom-left':
      default:
        x = padding;
        y = canvas.height - bubbleSize - padding;
        break;
    }

    // Draw circular webcam bubble with border
    ctx.save();
    ctx.beginPath();
    ctx.arc(x + bubbleSize / 2, y + bubbleSize / 2, bubbleSize / 2, 0, Math.PI * 2);
    ctx.clip();
    
    // Draw webcam video (maintaining aspect ratio, centered)
    const videoWidth = this.webcamVideo.videoWidth || 320;
    const videoHeight = this.webcamVideo.videoHeight || 320;
    const scale = Math.max(bubbleSize / videoWidth, bubbleSize / videoHeight);
    const scaledWidth = videoWidth * scale;
    const scaledHeight = videoHeight * scale;
    const offsetX = x + (bubbleSize - scaledWidth) / 2;
    const offsetY = y + (bubbleSize - scaledHeight) / 2;
    
    ctx.drawImage(this.webcamVideo, offsetX, offsetY, scaledWidth, scaledHeight);
    ctx.restore();

    // Draw border around bubble (only if borderWidth > 0)
    if (borderWidth > 0) {
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = borderWidth;
      ctx.beginPath();
      ctx.arc(x + bubbleSize / 2, y + bubbleSize / 2, bubbleSize / 2 - (borderWidth / 2), 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  
  /**
   * Monitor stream health and detect frozen frames
   */
  private startStreamHealthMonitoring(): void {
    // Check every 3 seconds for better responsiveness
    this.streamHealthInterval = setInterval(() => {
      if (!this.isCompositingActive) {
        this.stopStreamHealthMonitoring();
        return;
      }
      
      const now = Date.now();
      const screenStaleMs = now - this.lastScreenFrameTime;
      const webcamStaleMs = now - this.lastWebcamFrameTime;
      
      // If screen hasn't updated in 10 seconds, there's a serious problem
      if (screenStaleMs > 10000 && this.screenStream) {
        console.error('Screen stream appears completely frozen (no frames for 10+ seconds)');
        
        if (this._streamWarning.value !== 'screen-frozen') {
          this._streamWarning.next('screen-frozen');
        }
        
        // Check if track is still live
        const screenTrack = this.screenStream.getVideoTracks()[0];
        if (screenTrack) {
          console.log('Screen track state:', screenTrack.readyState, 'muted:', screenTrack.muted, 'enabled:', screenTrack.enabled);
          
          if (screenTrack.readyState === 'ended') {
            this._error.next('Screen sharing was stopped');
            this.stopRecording();
            return;
          }
          
          // Try to recover by re-playing the video element
          if (this.screenVideo) {
            console.log('Attempting to recover screen video...');
            this.screenVideo.play().catch(e => {
              console.error('Failed to restart screen video:', e);
            });
          }
        }
      }
      
      // Webcam frozen - warn but don't stop recording
      if (webcamStaleMs > 10000 && this.webcamStream) {
        console.warn('Webcam stream appears frozen');
        
        if (this._streamWarning.value === 'none') {
          this._streamWarning.next('webcam-frozen');
        }
        
        const webcamTrack = this.webcamStream.getVideoTracks()[0];
        if (webcamTrack && webcamTrack.readyState === 'ended') {
          console.warn('Webcam track ended - disabling webcam overlay');
          this.webcamStream = null;
          // Clear webcam warning if stream is just gone
          if (this._streamWarning.value === 'webcam-frozen') {
            this._streamWarning.next('none');
          }
        }
      }
      
      // Check audio stream health
      if (this.audioStream) {
        const audioTrack = this.audioStream.getAudioTracks()[0];
        if (audioTrack && audioTrack.readyState === 'ended') {
          console.warn('Audio track ended');
          if (this._streamWarning.value === 'none') {
            this._streamWarning.next('audio-lost');
          }
        }
      }
      
    }, 3000);
  }
  
  /**
   * Stop stream health monitoring
   */
  private stopStreamHealthMonitoring(): void {
    if (this.streamHealthInterval) {
      clearInterval(this.streamHealthInterval);
      this.streamHealthInterval = null;
    }
  }

  /**
   * Start the recording timer
   */
  private startRecordingTimer(): void {
    this.stopTimer$.next();
    interval(1000)
      .pipe(takeUntil(this.stopTimer$))
      .subscribe(() => {
        const elapsed = Math.floor((Date.now() - this.recordingStartTime) / 1000);
        this._recordingTime.next(elapsed);
      });
  }

  /**
   * Stop the recording
   */
  stopRecording(): void {
    if (this._state.value !== 'recording' && this._state.value !== 'paused') {
      return;
    }

    if (this.isNativeRecording) {
      this.stopTimer$.next();
      this._isPaused.next(false);
      this._state.next('processing');
      void this.nativeHostService.stopScreenRecording();
      return;
    }

    this.stopTimer$.next();
    this._isPaused.next(false);
    this._state.next('processing');

    // Stop compositing loop
    this.isCompositingActive = false;
    
    // Stop animation loop
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Stop media recorder
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    // Stop all streams
    this.stopAllStreams();
  }

  /**
   * Pause the recording
   */
  pauseRecording(): void {
    if (this._state.value !== 'recording' || !this.mediaRecorder) {
      if (this.isNativeRecording && this._state.value === 'recording') {
        void this.nativeHostService.pauseScreenRecording();
        this._isPaused.next(true);
        this._state.next('paused');
        this.stopTimer$.next();
      }
      return;
    }

    if (this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause();
      this._isPaused.next(true);
      this._state.next('paused');
      this.stopTimer$.next(); // Stop the timer while paused
    }
  }

  /**
   * Resume the recording
   */
  resumeRecording(): void {
    if (this._state.value !== 'paused' || !this.mediaRecorder) {
      if (this.isNativeRecording && this._state.value === 'paused') {
        void this.nativeHostService.resumeScreenRecording();
        this._isPaused.next(false);
        this._state.next('recording');
        const currentTime = this._recordingTime.value;
        this.recordingStartTime = Date.now() - (currentTime * 1000);
        this.startRecordingTimer();
      }
      return;
    }

    if (this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume();
      this._isPaused.next(false);
      this._state.next('recording');
      
      // Resume the timer from where we left off
      const currentTime = this._recordingTime.value;
      this.recordingStartTime = Date.now() - (currentTime * 1000);
      this.startRecordingTimer();
    }
  }

  /**
   * Discard the recording without uploading
   */
  discardRecording(): void {
    if (this._state.value !== 'recording' && this._state.value !== 'paused') {
      return;
    }

    if (this.isNativeRecording) {
      this.stopTimer$.next();
      this._isPaused.next(false);
      this.isNativeRecording = false;
      void this.nativeHostService.discardScreenRecording();
      this.cleanup();
      this._state.next('idle');
      this._recordingTime.next(0);
      this._isMinimized.next(false);
      return;
    }

    this.stopTimer$.next();
    this._isPaused.next(false);
    
    // Stop compositing loop
    this.isCompositingActive = false;
    
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Clear recorded chunks so nothing gets uploaded
    this.recordedChunks = [];

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      // Remove the onstop handler to prevent upload
      this.mediaRecorder.onstop = null;
      this.mediaRecorder.stop();
    }

    this.cleanup();
    this._state.next('idle');
    this._recordingTime.next(0);
    this._isMinimized.next(false);
  }

  /**
   * Cancel the recording
   */
  cancelRecording(): void {
    this.stopTimer$.next();
    this._isPaused.next(false);

    if (this.isNativeRecording) {
      this.isNativeRecording = false;
      void this.nativeHostService.discardScreenRecording();
      this.cleanup();
      this._state.next('idle');
      this._recordingTime.next(0);
      this._isModalVisible.next(false);
      return;
    }
    
    // Stop compositing loop
    this.isCompositingActive = false;
    
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    this.cleanup();
    this._state.next('idle');
    this._recordingTime.next(0);
    this._isModalVisible.next(false);
  }

  /**
   * Handle recording complete - create blob and upload
   */
  private async handleRecordingComplete(): Promise<void> {
    if (this.recordedChunks.length === 0) {
      this._error.next('No recording data captured.');
      this._state.next('error');
      return;
    }

    // Determine file extension based on mime type
    const mimeType = this.mediaRecorder?.mimeType || 'video/webm';
    const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
    
    const blob = new Blob(this.recordedChunks, { type: mimeType });
    const duration = Math.floor((Date.now() - this.recordingStartTime) / 1000);

    // Upload to Firebase Storage
    await this.uploadRecording(blob, extension, duration);
  }

  /**
   * Upload the recording to Firebase Storage
   */
  private async uploadRecording(blob: Blob, extension: string, duration: number): Promise<void> {
    if (!this.currentConfig) {
      this._error.next('Recording configuration not found.');
      this._state.next('error');
      return;
    }

    const timestamp = Date.now();
    const fileName = `screen-recording-${timestamp}.${extension}`;
    const folder = this.currentConfig.folder || 'screen-recordings';
    const path = `media/${this.currentConfig.userId}/${folder}/${fileName}`;

    try {
      // Create media document FIRST (without URL) so share link is immediately available
      const mediaDoc = {
        // Core fields expected by media component query
        uid: this.currentConfig.userId,
        folder: 'support', // Must be 'support' to show in Files page
        
        // Standard media fields
        type: 'video/' + extension,
        name: 'support ' + this.currentConfig.userId,
        displayName: this.currentConfig.title || `Screen Recording ${new Date().toLocaleDateString()}`,
        description: this.currentConfig.userId,
        url: null, // Will be updated after upload completes
        time: new Date(),
        size: blob.size,
        pinned: false,
        
        // Additional metadata for screen recordings
        fileExtension: extension,
        file: fileName,
        duration: duration,
        tags: ['screen-recording'],
        archived: false,
        createdAt: new Date(),
        uploadDate: new Date()
      };

      const docRef = await this.afs.collection('media').add(mediaDoc);
      
      // Emit mediaId immediately so share link is available
      this._mediaId.next(docRef.id);
      
      // Go to complete state immediately - share link works, but upload still in progress
      this._state.next('complete');
      this._uploadProgress.next(0);

      // Start upload in background
      const ref = this.afStorage.ref(path);
      const task = ref.put(blob, {
        contentType: blob.type,
        customMetadata: {
          duration: duration.toString(),
          title: this.currentConfig.title || 'Screen Recording',
          createdAt: new Date().toISOString()
        }
      });

      // Track upload progress
      task.percentageChanges().subscribe((progress) => {
        if (progress !== undefined) {
          this._uploadProgress.next(Math.round(progress));
        }
      });

      // Wait for upload to complete
      await task;

      // Get download URL
      const downloadUrl = await ref.getDownloadURL().toPromise();

      // Update the media document with the download URL
      await this.afs.doc(`media/${docRef.id}`).update({
        url: downloadUrl
      });

      // Now emit the download URL
      this._downloadUrl.next(downloadUrl);
      this._uploadProgress.next(100);

      const result: RecordingResult = {
        downloadUrl,
        mediaId: docRef.id,
        duration,
        createdAt: new Date()
      };

      // Call completion callback if provided
      if (this.onComplete) {
        this.onComplete(result);
      }

    } catch (error: any) {
      console.error('Upload error:', error);
      this._error.next('Failed to upload recording: ' + (error.message || 'Unknown error'));
      this._state.next('error');
    }
  }

  /**
   * Stop all media streams
   */
  private stopAllStreams(): void {
    if (this.screenStream) {
      this.screenStream.getTracks().forEach(track => track.stop());
      this.screenStream = null;
    }
    if (this.webcamStream) {
      this.webcamStream.getTracks().forEach(track => track.stop());
      this.webcamStream = null;
    }
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }
  }

  /**
   * Cleanup all resources
   */
  private cleanup(): void {
    this.stopAllStreams();
    this.stopStreamHealthMonitoring();
    
    // Remove visibility change listener
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }
    
    // Clear compositing interval if using interval mode
    if (this.compositeIntervalId) {
      clearInterval(this.compositeIntervalId);
      this.compositeIntervalId = null;
    }

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.screenVideo) {
      this.screenVideo.pause();
      this.screenVideo.srcObject = null;
      this.screenVideo.onerror = null;
      this.screenVideo.onstalled = null;
      this.screenVideo = null;
    }

    if (this.webcamVideo) {
      this.webcamVideo.pause();
      this.webcamVideo.srcObject = null;
      this.webcamVideo.onerror = null;
      this.webcamVideo.onstalled = null;
      this.webcamVideo = null;
    }

    this.compositeCanvas = null;
    this.compositeCtx = null;
    this.canvasCaptureTrack = null;
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.currentConfig = null;
    this.onComplete = null;
    this.isNativeRecording = false;
    
    // Reset tracking variables
    this.frameCount = 0;
    this._streamWarning.next('none');
    this._actualFps.next(30);
  }

  /**
   * Reset state to idle
   */
  reset(): void {
    this.cleanup();
    this._state.next('idle');
    this._recordingTime.next(0);
    this._uploadProgress.next(0);
    this._countdown.next(0);
    this._downloadUrl.next(null);
    this._mediaId.next(null);
    this._error.next(null);
    this._streamWarning.next('none');
    this._actualFps.next(30);
  }

  /**
   * Copy download URL to clipboard
   */
  async copyToClipboard(): Promise<boolean> {
    const url = this._downloadUrl.value;
    if (!url || !isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch (e) {
      console.error('Failed to copy to clipboard:', e);
      return false;
    }
  }

  /**
   * Format seconds to MM:SS display
   */
  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Get current state value (for synchronous checks)
   */
  get currentState(): RecordingState {
    return this._state.value;
  }

  /**
   * Get current recording time (for synchronous checks)
   */
  get currentRecordingTime(): number {
    return this._recordingTime.value;
  }

  /**
   * Check if currently recording
   */
  get isRecording(): boolean {
    return this._state.value === 'recording';
  }

  /**
   * Update media document metadata (title, description)
   */
  async updateMediaMetadata(mediaId: string, metadata: { title?: string; description?: string }): Promise<void> {
    if (!mediaId) return;
    
    const updates: any = {};
    if (metadata.title !== undefined) {
      updates.title = metadata.title;
    }
    if (metadata.description !== undefined) {
      updates.description = metadata.description;
    }
    
    await this.afs.doc(`media/${mediaId}`).update(updates);
  }

  async getRecordingHistory(userId: string, limit?: number): Promise<ScreenRecordingHistoryItem[]> {
    if (!userId) {
      return [];
    }

    const collection = limit
      ? this.afs.collection('media', ref => ref.where('uid', '==', userId).limit(limit))
      : this.afs.collection('media', ref => ref.where('uid', '==', userId));
    const snapshot = await collection.get().toPromise();

    const docs = snapshot?.docs || [];
    return docs
      .map(doc => this.mapMediaDocToHistoryItem(doc.id, doc.data()))
      .filter((item): item is ScreenRecordingHistoryItem => item !== null)
      .sort((left, right) => {
        const leftTime = left.createdAt?.getTime() || 0;
        const rightTime = right.createdAt?.getTime() || 0;
        return rightTime - leftTime;
      });
  }

  private mapMediaDocToHistoryItem(id: string, rawData: unknown): ScreenRecordingHistoryItem | null {
    const data = rawData as any;
    if (!this.isVideoMedia(data)) {
      return null;
    }

    return {
      id,
      displayName: data?.displayName || data?.title || data?.name || 'Untitled recording',
      url: typeof data?.url === 'string' && data.url.length > 0 ? data.url : null,
      type: data?.type || data?.mimeType || 'video',
      duration: typeof data?.duration === 'number' ? data.duration : null,
      size: typeof data?.size === 'number' ? data.size : null,
      createdAt: this.getMediaDate(data)
    };
  }

  private isVideoMedia(data: any): boolean {
    const type = `${data?.type || data?.mimeType || ''}`.toLowerCase();
    const file = `${data?.file || data?.fileExtension || data?.name || ''}`.toLowerCase();
    const tags = Array.isArray(data?.tags) ? data.tags : [];

    return type.startsWith('video/') ||
      tags.includes('screen-recording') ||
      file.endsWith('.webm') ||
      file.endsWith('.mp4') ||
      file === 'webm' ||
      file === 'mp4';
  }

  private getMediaDate(data: any): Date | null {
    const value = data?.time || data?.createdAt || data?.uploadDate;
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value;
    }

    if (typeof value.toDate === 'function') {
      return value.toDate();
    }

    if (typeof value === 'number' || typeof value === 'string') {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    return null;
  }
}
